import { useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Autocomplete,
  Alert, CircularProgress, Typography, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Chip,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createReceipt, allocateReceipt, cancelReceipt, getReceipt, getClientOpenInvoices, getClientAdvanceBalance, listCashAccounts,
  type ReceiptListItem, type SaveReceiptLinePayload,
} from '../../api/financiar'
import { listClients } from '../../api/clienti'
import { errorMessage } from '../../api/client'

export default function ReceiptDialog({ target, onClose }: { target: ReceiptListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const [clientId, setClientId] = useState('')
  const [cashAccountId, setCashAccountId] = useState('')
  const [currency, setCurrency] = useState('RON')
  const [exchangeRate, setExchangeRate] = useState('1')
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [notes, setNotes] = useState('')
  const [lineAmounts, setLineAmounts] = useState<Record<string, string>>({})

  useEffect(() => {
    if (target === 'new') {
      setClientId(''); setCashAccountId(''); setCurrency('RON'); setExchangeRate('1'); setAmount('');
      setDate(new Date().toISOString().slice(0, 10)); setNotes(''); setLineAmounts({});
    }
  }, [target])

  const { data: clients } = useQuery({ queryKey: ['comercial', 'clienti', 'toti'], queryFn: () => listClients(), enabled: open && !editing })
  const { data: accounts } = useQuery({ queryKey: ['financiar', 'conturi'], queryFn: listCashAccounts, enabled: open && !editing })
  const { data: detail } = useQuery({ queryKey: ['financiar', 'incasare', editing ? (target as ReceiptListItem).id : null], queryFn: () => getReceipt((target as ReceiptListItem).id), enabled: editing })
  const effectiveClientId = editing ? detail?.clientId : clientId
  const { data: openInvoices } = useQuery({
    queryKey: ['financiar', 'facturi-deschise', effectiveClientId], queryFn: () => getClientOpenInvoices(effectiveClientId!), enabled: !!effectiveClientId,
  })
  const { data: advance } = useQuery({
    queryKey: ['financiar', 'sold-avans-client', effectiveClientId], queryFn: () => getClientAdvanceBalance(effectiveClientId!), enabled: !!effectiveClientId,
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['financiar', 'incasari'] })
    void qc.invalidateQueries({ queryKey: ['financiar', 'incasare'] })
    void qc.invalidateQueries({ queryKey: ['financiar', 'facturi-deschise'] })
    void qc.invalidateQueries({ queryKey: ['financiar', 'sold-avans-client'] })
  }

  const createMutation = useMutation({
    mutationFn: () => {
      const lines: SaveReceiptLinePayload[] = Object.entries(lineAmounts).filter(([, v]) => Number(v) > 0)
        .map(([invoiceId, v]) => ({ invoiceId, amount: Number(v) }))
      return createReceipt({ date, clientId, cashAccountId, currency, exchangeRate: Number(exchangeRate), amount: Number(amount), notes: notes || null, lines })
    },
    onSuccess: () => { invalidate(); onClose() },
  })
  const allocateMutation = useMutation({
    mutationFn: () => {
      const lines: SaveReceiptLinePayload[] = Object.entries(lineAmounts).filter(([, v]) => Number(v) > 0)
        .map(([invoiceId, v]) => ({ invoiceId, amount: Number(v) }))
      return allocateReceipt((target as ReceiptListItem).id, lines)
    },
    onSuccess: () => { invalidate(); setLineAmounts({}) },
  })
  const cancelMutation = useMutation({
    mutationFn: () => cancelReceipt((target as ReceiptListItem).id),
    onSuccess: () => { invalidate(); onClose() },
  })

  const alreadyAllocatedIds = new Set(detail?.allocations.map((a) => a.invoiceId) ?? [])
  const availableInvoices = (openInvoices ?? []).filter((i) => !alreadyAllocatedIds.has(i.invoiceId))
  const totalAmount = editing ? (detail?.amount ?? 0) : Number(amount) || 0
  const alreadyAllocated = detail?.allocations.reduce((s, a) => s + a.allocatedAmount, 0) ?? 0
  const remaining = totalAmount - alreadyAllocated - Object.values(lineAmounts).reduce((s, v) => s + (Number(v) || 0), 0);

  if (editing && !detail) {
    return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth><DialogContent><Stack sx={{ py: 4, alignItems: 'center' }}><CircularProgress /></Stack></DialogContent></Dialog>
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Încasare ${detail?.number}` : 'Încasare nouă'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {editing && detail?.cancelledAt && <Alert severity="warning">Încasare anulată.</Alert>}
          {!editing ? (
            <>
              <Autocomplete
                options={clients?.map((c) => c.id) ?? []}
                getOptionLabel={(id) => clients?.find((c) => c.id === id)?.name ?? id}
                value={clientId || null} onChange={(_, v) => { setClientId(v ?? ''); setLineAmounts({}) }}
                renderInput={(params) => <TextField {...params} label="Client" />}
              />
              <Stack direction="row" spacing={2}>
                <TextField select label="Cont" value={cashAccountId} onChange={(e) => setCashAccountId(e.target.value)} fullWidth>
                  {accounts?.map((a) => <MenuItem key={a.id} value={a.id}>{a.name} ({a.currency})</MenuItem>)}
                </TextField>
                <TextField type="date" label="Dată" value={date} onChange={(e) => setDate(e.target.value)} sx={{ width: 170 }} slotProps={{ inputLabel: { shrink: true } }} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="Monedă" value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ width: 110 }} />
                {currency !== 'RON' && <TextField type="number" label="Curs/RON" value={exchangeRate} onChange={(e) => setExchangeRate(e.target.value)} sx={{ width: 140 }} />}
                <TextField type="number" label="Sumă încasată" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth />
              </Stack>
              <TextField label="Note" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Client: {detail?.clientName} · Cont: {detail?.cashAccountName} · Dată: {detail?.date} · Sumă: {detail?.amount.toFixed(2).replace('.', ',')} {detail?.currency}
            </Typography>
          )}

          {advance && advance.balance > 0 && <Alert severity="info">Sold de avans disponibil pentru acest client: {advance.balance.toFixed(2).replace('.', ',')}.</Alert>}

          {editing && detail && detail.allocations.length > 0 && (
            <Table size="small">
              <TableHead><TableRow><TableCell>Factură</TableCell><TableCell align="right">Alocat</TableCell></TableRow></TableHead>
              <TableBody>
                {detail.allocations.map((a) => (
                  <TableRow key={a.id}><TableCell>{a.invoiceNumber}</TableCell><TableCell align="right">{a.allocatedAmount.toFixed(2).replace('.', ',')}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {(!editing || !detail?.cancelledAt) && effectiveClientId && availableInvoices.length > 0 && (
            <>
              <Typography variant="subtitle2">Alocare pe facturi deschise {editing && '(decontare avans)'}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell padding="checkbox" /><TableCell>Factură</TableCell><TableCell align="right">Sold rămas</TableCell><TableCell align="right">Alocă</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {availableInvoices.map((inv) => (
                    <TableRow key={inv.invoiceId}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={!!lineAmounts[inv.invoiceId]}
                          onChange={(e) => setLineAmounts((m) => ({ ...m, [inv.invoiceId]: e.target.checked ? String(Math.min(inv.outstanding, Math.max(remaining, 0) || inv.outstanding)) : '' }))} />
                      </TableCell>
                      <TableCell>{inv.number} <Chip size="small" label={`scad. ${inv.dueDate}`} /></TableCell>
                      <TableCell align="right">{inv.outstanding.toFixed(2).replace('.', ',')}</TableCell>
                      <TableCell align="right">
                        <TextField type="number" size="small" sx={{ width: 100 }} disabled={!lineAmounts[inv.invoiceId]}
                          value={lineAmounts[inv.invoiceId] ?? ''} onChange={(e) => setLineAmounts((m) => ({ ...m, [inv.invoiceId]: e.target.value }))} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary">Nealocat: {remaining.toFixed(2).replace('.', ',')}</Typography>
            </>
          )}

          {(createMutation.isError || allocateMutation.isError || cancelMutation.isError) && (
            <Alert severity="error">{errorMessage(createMutation.error ?? allocateMutation.error ?? cancelMutation.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
        {editing && !detail?.cancelledAt && (
          <>
            {Object.values(lineAmounts).some((v) => Number(v) > 0) && (
              <Button onClick={() => allocateMutation.mutate()} disabled={allocateMutation.isPending}>Salvează alocarea</Button>
            )}
            <Button color="error" onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending}>Anulează încasarea</Button>
          </>
        )}
        {!editing && (
          <Button variant="contained" disabled={!clientId || !cashAccountId || !amount || createMutation.isPending}
            startIcon={createMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => createMutation.mutate()}>
            Salvează
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
